import React from 'react';
import { Label } from '../../../components/ui/label';
import { useT } from '../../salesI18n';
import { InchesInput, fmtInches } from './InchesInput';
import { DOOR_MODELS, TRANSOM_MODES, HANDLE_STYLES, normalizeDoor } from './doorGeometry';
import { OptionIcon } from '../../../components/options/OptionIcon';

const Pills = ({ options, value, onChange, label, testId, disabled = () => false }) => (
  <div className="inline-flex rounded-full border border-slate-200 bg-white/70 overflow-hidden text-xs font-semibold" data-testid={testId}>
    {options.map((o, i) => <button key={o} type="button" disabled={disabled(o)} onClick={() => onChange(o)} aria-pressed={value === o} className={`px-3 h-9 ${i > 0 ? 'border-l border-slate-200' : ''} ${value === o ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-white'} disabled:opacity-40 disabled:cursor-not-allowed`} data-testid={`${testId}-${o}`}>{label(o)}</button>)}
  </div>
);

const SizeRow = ({ label, part, onChange, testId }) => {
  const { t } = useT();
  return (
    <div className="grid grid-cols-[64px_1fr_1fr] gap-1.5 items-end" data-testid={testId}>
      <span className="h-11 rounded-lg bg-white border border-teal-200 text-teal-800 text-[11px] font-bold flex items-center px-1.5 truncate" title={label}>{label}</span>
      <div><Label className="text-[10px] text-slate-500">{t.measureShort.width}</Label><InchesInput testId={`${testId}-width`} value={part.width ?? ''} onChange={(v) => onChange({ ...part, width: v })} /></div>
      <div><Label className="text-[10px] text-slate-500">{t.measureShort.height}</Label><InchesInput testId={`${testId}-height`} value={part.height ?? ''} onChange={(v) => onChange({ ...part, height: v })} /></div>
    </div>
  );
};

// Entry Door: folha (simples/dupla), sidelights (0–2 + lado), transom (nenhum / 1 sobre tudo / 1 sobre cada) e a medida de cada peça.
// width/height da abertura = vão total; a folha da porta é derivada (total − sidelights; altura − transom).
export const DoorLayoutFields = ({ value, onChange, width, height, handles = HANDLE_STYLES }) => {
  const { t } = useT();
  const d = normalizeDoor(value);
  const set = (patch) => onChange(normalizeDoor({ ...d, ...patch }));
  const slW = d.sidelight_sizes.reduce((a, p) => a + (Number(p.width) || 0), 0);
  const trH = Math.max(0, ...d.transom_sizes.map((p) => Number(p.height) || 0));
  const slabW = Math.max(0, (Number(width) || 0) - slW), slabH = Math.max(0, (Number(height) || 0) - trH);
  return (
    <div className="rounded-2xl border border-teal-100 bg-teal-50/40 p-3 space-y-3" data-testid="opening-door-layout">
      <div>
        <p className="text-sm font-semibold text-slate-900">{t.doorLayoutTitle}</p>
        <p className="text-[11px] text-slate-500">{t.doorLayoutHint}</p>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">{t.entryModel}</Label>
          <div className="mt-1"><Pills options={DOOR_MODELS} value={d.model} onChange={(m) => set({ model: m })} label={(m) => t.entryModels[m]} testId="opening-door-model" /></div>
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">{t.sidelights}</Label>
          <div className="mt-1"><Pills options={[0, 1, 2]} value={d.sidelights} onChange={(n) => set({ sidelights: n })} label={(n) => String(n)} testId="opening-door-sidelights" /></div>
        </div>
        {d.sidelights === 1 && (
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">{t.sidelightSide}</Label>
            <div className="mt-1"><Pills options={['left', 'right']} value={d.sidelight_side} onChange={(s) => set({ sidelight_side: s })} label={(s) => t.sides[s]} testId="opening-door-sidelight-side" /></div>
          </div>
        )}
      </div>
      <div>
        <Label className="text-[11px] font-semibold text-slate-600">{t.transomLabel}</Label>
        <div className="mt-1"><Pills options={TRANSOM_MODES} value={d.transom} onChange={(m) => set({ transom: m })} label={(m) => t.transomModes[m]} disabled={(m) => m === 'each' && d.sidelights === 0} testId="opening-door-transom" /></div>
        {d.sidelights === 0 && <p className="text-[11px] text-slate-500 mt-1" data-testid="opening-door-transom-hint">{t.transomEachHint}</p>}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">{d.model === 'double' ? t.activeLeafLabel : t.hingeLabel}</Label>
          <div className="mt-1"><Pills options={['left', 'right']} value={d.hinge} onChange={(h) => set({ hinge: h })} label={(s) => t.sides[s]} testId="opening-door-hinge" /></div>
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">{t.handleLabel}</Label>
          <div className="mt-1"><Pills options={HANDLE_STYLES} value={d.handle} onChange={(h) => set({ handle: h })} label={(h) => <span className="inline-flex items-center gap-1"><OptionIcon kind="handle" value={h} size={20} /> {t.handleStyles[h]}</span>} disabled={(h) => !handles.includes(h)} testId="opening-door-handle" /></div>
        </div>
      </div>
      {(d.sidelight_sizes.length > 0 || d.transom_sizes.length > 0) && (
        <div className="space-y-1.5" data-testid="opening-door-parts">
          {d.sidelight_sizes.map((p, i) => <SizeRow key={`sl${i}`} label={d.sidelights === 1 ? t.sidelightOne : t.sidelightN(i + 1)} part={p} onChange={(v) => set({ sidelight_sizes: d.sidelight_sizes.map((x, j) => (j === i ? v : x)) })} testId={`opening-door-sidelight-${i}`} />)}
          {d.transom_sizes.map((p, i) => <SizeRow key={`tr${i}`} label={d.transom === 'single' ? t.transomOne : t.transomN(i + 1)} part={p} onChange={(v) => set({ transom_sizes: d.transom_sizes.map((x, j) => (j === i ? v : x)) })} testId={`opening-door-transom-${i}`} />)}
        </div>
      )}
      {Number(width) > 0 && Number(height) > 0 && (
        <p className="text-[11px] text-slate-600 font-mono" data-testid="opening-door-slab-size">{t.doorSlabSize}: <b>{fmtInches(slabW)}" × {fmtInches(slabH)}"</b>{d.model === 'double' && <span className="text-slate-500"> · {t.doorEachLeaf}: {fmtInches(slabW / 2)}"</span>}</p>
      )}
    </div>
  );
};
